import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import {
  Sun,
  BatteryCharging,
  Home,
  TrendingUp,
  TrendingDown,
  Wallet,
  Clock,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Zap,
  Leaf,
  MapPin,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { AREAS, AREA_DATA_NOTE, AREA_LIST } from '../data/areas';
import { LOGO_URL } from '../data/mock';
import { LEGAL_ENTITY } from '../components/Footer';
import { Seo, SITE_URL, SITE_NAME } from '../components/Seo';

const useCountUp = (target, duration = 1500, start = true) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(eased * target);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return value;
};

const StatCard = ({ value, suffix = '', prefix = '', label, sub, testid, animate }) => {
  const v = useCountUp(value, 1500, animate);
  return (
    <div
      className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6 shadow-sm text-center"
      data-testid={testid}
    >
      <p className="text-3xl sm:text-4xl font-black tabular-nums text-cyo-orange leading-none">
        {prefix}
        {Math.round(v).toLocaleString()}
        {suffix}
      </p>
      <p className="mt-2 text-sm font-bold text-gray-900">{label}</p>
      <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
    </div>
  );
};

const FaqItem = ({ q, a, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid={`${testid}-toggle`}
      >
        <span className="text-sm sm:text-base font-bold text-gray-900">{q}</span>
        <ChevronDown
          className={`w-5 h-5 text-cyo-orange flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <p className="px-5 pb-5 -mt-1 text-sm text-gray-600 leading-relaxed">{a}</p>}
    </div>
  );
};

const AreaPage = () => {
  const { area } = useParams();
  const data = AREAS[(area || '').toLowerCase()];
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (data) {
      window.scrollTo(0, 0);
    }
    const t = setTimeout(() => setAnimate(true), 200);
    return () => clearTimeout(t);
  }, [data]);

  if (!data) return <Navigate to="/" replace />;

  const { name, state, region, suburbs, stats, typicalSolar, typicalBattery, payback, setups } = data;

  const faqs = [
    {
      q: `Is solar worth it in ${name}?`,
      a: `Yes — with around ${stats.homesWithSolar}% of ${name} homes already running solar and typical savings near $${stats.avgSavings.toLocaleString()} a year, locals are seeing strong returns. Rising electricity prices across metro ${state} make solar increasingly valuable each year.`,
    },
    {
      q: `How much could I save on electricity?`,
      a: `${name} households typically cut their power bills by up to ${stats.billReduction}% with a well-sized solar system. Adding a battery can push savings further by using stored energy during expensive evening peak hours.`,
    },
    {
      q: `Can I add a battery later?`,
      a: `Absolutely. Many homeowners start with solar and add storage later. Systems can be designed battery-ready. Battery uptake in ${name} is growing around ${stats.batteryGrowth}% year-on-year.`,
    },
    {
      q: `What size system suits a ${name} home?`,
      a: `A ${typicalSolar}kW solar system paired with a ${typicalBattery}kWh battery is a popular fit for many ${name} homes, but the right size depends on your roof, usage and budget. A quick comparison gives you a tailored recommendation.`,
    },
    {
      q: `Are rebates available in ${name}?`,
      a: `Eligible Victorian households can access rebates under Solar Victoria's Solar Homes Program (eligibility criteria and amounts apply and may change). We'll help you understand what you may qualify for when you compare.`,
    },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="area-page">
      <Seo
        title={`Solar & Battery in ${name}, ${state} | ${SITE_NAME}`}
        description={`Compare solar & battery options in ${name}, ${state}. Around ${stats.homesWithSolar}% of local homes have solar, saving ~$${stats.avgSavings.toLocaleString()} a year. See local savings, rebates and typical setups.`}
        path={`/${data.slug}`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: `Solar in ${name}`, item: `${SITE_URL}/${data.slug}` },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
        ]}
      />
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" data-testid="area-logo-link" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CompareYourOptions.com.au" className="h-8 w-auto" />
          </Link>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-cyo-orange bg-orange-50 border border-orange-100 px-3 py-1 rounded-full">
            <MapPin className="w-3.5 h-3.5" />
            {name}, {state}
          </span>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{ background: 'radial-gradient(120% 100% at 0% 0%, #FFF4E6 0%, #FFFFFF 55%, #E8F8F5 100%)' }}
        data-testid="area-hero"
      >
        <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-orange-200/40 rounded-full blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-4 pt-10 pb-12 sm:pt-14 sm:pb-16">
          <div className="inline-flex items-center gap-2 bg-white border border-orange-200 shadow-sm px-3 py-1.5 rounded-full">
            <Sparkles className="w-4 h-4 text-cyo-orange" />
            <span className="text-xs font-bold tracking-wide text-gray-800">
              Local solar &amp; battery insights
            </span>
          </div>

          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight max-w-3xl">
            Solar &amp; battery in{' '}
            <span className="text-cyo-orange">{name}</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-gray-600 max-w-2xl leading-relaxed">
            Join the many households across {region} ({suburbs.slice(0, 3).join(', ')} and
            nearby) already cutting power bills with solar and battery storage. Compare your
            options free in under 2 minutes.
          </p>

          {/* Stat cards */}
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              value={stats.homesWithSolar}
              suffix="%"
              label="Homes with solar"
              sub={`across ${name}`}
              testid="area-stat-penetration"
              animate={animate}
            />
            <StatCard
              value={stats.avgSavings}
              prefix="$"
              label="Avg. yearly savings"
              sub="indicative*"
              testid="area-stat-savings"
              animate={animate}
            />
            <StatCard
              value={stats.billReduction}
              suffix="%"
              label="Bill reduction"
              sub="up to*"
              testid="area-stat-reduction"
              animate={animate}
            />
            <StatCard
              value={stats.batteryGrowth}
              suffix="%"
              label="Battery growth"
              sub="year-on-year"
              testid="area-stat-battery"
              animate={animate}
            />
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/compare"
              data-testid="area-hero-cta"
              className="inline-flex items-center justify-center gap-2 bg-cyo-orange hover:bg-orange-600 text-white font-bold px-7 py-4 rounded-full shadow-lg hover:-translate-y-0.5 transition-all"
            >
              Compare My Options
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#area-savings"
              data-testid="area-hero-secondary"
              className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-800 font-bold px-7 py-4 rounded-full hover:border-cyo-orange/40 transition-all"
            >
              See local savings
              <ChevronDown className="w-4 h-4" />
            </a>
          </div>
          <p className="mt-3 text-xs text-gray-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            100% free &amp; independent • no obligation
          </p>
        </div>
      </section>

      {/* WHY IT MAKES SENSE */}
      <section className="max-w-5xl mx-auto px-4 py-14 sm:py-20" data-testid="area-why">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            Why solar &amp; batteries make
            <br className="hidden sm:block" /> sense in <span className="text-cyo-orange">{name}</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {[
            {
              icon: TrendingUp,
              tone: 'orange',
              title: 'High solar uptake',
              body: `Around ${stats.homesWithSolar}% of ${name} homes already have solar — strong local confidence in the technology.`,
            },
            {
              icon: Home,
              tone: 'blue',
              title: 'Ideal housing',
              body: `A mix of established homes and newer estates across ${region} — great roof space for generation.`,
            },
            {
              icon: TrendingDown,
              tone: 'orange',
              title: 'Rising power costs',
              body: `Metro ${state} electricity prices keep climbing, making solar more valuable each year.`,
            },
            {
              icon: BatteryCharging,
              tone: 'blue',
              title: 'Battery boom',
              body: `Battery adoption is growing about ${stats.batteryGrowth}% year-on-year as locals chase evening savings.`,
            },
          ].map(({ icon: Icon, tone, title, body }) => (
            <div key={title} className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 hover:shadow-lg transition-shadow">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  tone === 'orange' ? 'bg-orange-50 text-cyo-orange' : 'bg-blue-50 text-blue-600'
                }`}
              >
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-gray-900 leading-snug">{title}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SAVINGS SNAPSHOT */}
      <section
        id="area-savings"
        className="relative bg-gradient-to-b from-orange-50/60 via-white to-emerald-50/30 py-14 sm:py-20 scroll-mt-16"
        data-testid="area-savings"
      >
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 bg-cyo-orange/10 border border-cyo-orange/20 text-cyo-orange px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
              <Wallet className="w-3.5 h-3.5" />
              Local savings snapshot
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
              What {name} homes are saving
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
            {[
              { icon: Wallet, label: 'Average annual savings', value: `$${stats.avgSavings.toLocaleString()}`, sub: 'with solar + battery' },
              { icon: TrendingDown, label: 'Typical bill reduction', value: `${stats.billReduction}%`, sub: 'up to' },
              { icon: Clock, label: 'Typical payback', value: payback, sub: 'on a well-sized system' },
            ].map(({ icon: Icon, label, value, sub }) => (
              <div key={label} className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm text-center">
                <div className="w-12 h-12 rounded-xl bg-cyo-orange/10 text-cyo-orange flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6" />
                </div>
                <p className="text-3xl font-black text-gray-900 tabular-nums leading-none">{value}</p>
                <p className="mt-2 text-sm font-bold text-gray-900">{label}</p>
                <p className="text-xs text-gray-500">{sub}</p>
              </div>
            ))}
          </div>

          {/* Bill reduction bar */}
          <div className="mt-8 max-w-3xl mx-auto bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between text-sm font-bold text-gray-900">
              <span>Your bill today</span>
              <span>With solar + battery</span>
            </div>
            <div className="mt-3 h-4 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-cyo-orange to-emerald-500 transition-all duration-1000"
                style={{ width: animate ? `${stats.billReduction}%` : '0%' }}
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Indicative reduction of up to {stats.billReduction}% for a well-sized system in {name}.
            </p>
          </div>

          <p className="mt-6 text-[11px] text-gray-500 leading-relaxed text-center max-w-2xl mx-auto">
            ⚠️ Savings vary based on usage, system size, tariffs and household behaviour. {AREA_DATA_NOTE}
          </p>
        </div>
      </section>

      {/* TYPICAL LOCAL SETUPS */}
      <section className="max-w-5xl mx-auto px-4 py-14 sm:py-20" data-testid="area-setups">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            Typical local setups in {name}
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            Indicative examples of system sizes that suit homes around {region}.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
          {setups.map((s, i) => (
            <div
              key={`${s.suburb}-${i}`}
              data-testid={`area-setup-${i}`}
              className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                <MapPin className="w-4 h-4 text-cyo-orange" />
                {s.suburb}
              </div>
              <p className="mt-1 text-xs text-gray-500">{s.home}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-full">
                  <Sun className="w-3.5 h-3.5" /> {s.solar} solar
                </span>
                {s.battery && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full">
                    <BatteryCharging className="w-3.5 h-3.5" /> {s.battery}
                  </span>
                )}
              </div>
              <p className="mt-4 text-2xl font-black text-emerald-600 tabular-nums">{s.savings}</p>
              <p className="text-xs text-gray-500">est. annual savings (indicative)</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-[11px] text-gray-500 text-center max-w-2xl mx-auto">
          Examples are indicative only and not actual customer installations. Your tailored
          recommendation comes from a free comparison.
        </p>
      </section>

      {/* WHY COMPARE WITH CYO */}
      <section className="relative bg-gray-950 text-white py-14 sm:py-20" data-testid="area-why-cyo">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
              Why compare with us
            </h2>
            <p className="mt-3 text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
              Free, independent and built to help {name} households make a confident choice.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {[
              { icon: Wallet, title: '100% free & independent', body: 'Compare options at no cost, with no pressure and no obligation.' },
              { icon: ShieldCheck, title: 'Trusted local providers', body: 'We connect you with reputable, accredited installers who service your area.' },
              { icon: Zap, title: 'Solar, battery & more', body: 'Compare solar, batteries, EV chargers, heat pumps and heating & cooling in one place.' },
              { icon: Clock, title: 'Quick & easy', body: 'Get matched in about 2 minutes — your details stay private.' },
              { icon: Leaf, title: 'Rebate-aware', body: 'We help you understand the Victorian rebates you may be eligible for.' },
              { icon: CheckCircle2, title: 'Tailored to your home', body: 'Recommendations based on your roof, usage and goals — not a one-size-fits-all.' },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-6">
                <div className="w-11 h-11 rounded-xl bg-cyo-orange/20 text-cyo-orange flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold">{title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 py-14 sm:py-20" data-testid="area-faq">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            {name} solar &amp; battery FAQs
          </h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <FaqItem key={f.q} q={f.q} a={f.a} testid={`area-faq-${i}`} />
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="relative bg-gradient-to-br from-cyo-orange to-orange-600 text-white py-14 sm:py-20 overflow-hidden"
        data-testid="area-final-cta"
      >
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute -top-20 -left-20 w-80 h-80 bg-white/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-10 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <Sun className="w-10 h-10 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            See what solar &amp; battery
            <br />
            could look like for your {name} home
          </h2>
          <p className="mt-4 text-white/90 max-w-xl mx-auto text-sm sm:text-base">
            Join the {stats.homesWithSolar}% of {name} households already saving. Get your
            free, personalised comparison today.
          </p>
          <div className="mt-7">
            <Link
              to="/compare"
              data-testid="area-final-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyo-orange font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Compare My Options
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Other areas + footer */}
      <footer className="bg-gray-950 text-gray-400 py-10">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-300 mb-3">
            Other areas in {data.group}
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {AREA_LIST.filter((a) => a.group === data.group && a.slug !== data.slug)
              .slice(0, 14)
              .map((a) => (
                <Link
                  key={a.slug}
                  to={`/${a.slug}`}
                  data-testid={`area-link-${a.slug}`}
                  className="text-xs font-semibold text-gray-300 bg-white/5 border border-white/10 hover:border-cyo-orange/40 hover:text-white px-3 py-1.5 rounded-full transition-colors"
                >
                  Solar, Battery &amp; Energy Solutions in {a.name}
                </Link>
              ))}
          </div>

          <Link to="/" className="inline-block">
            <img src={LOGO_URL} alt={LEGAL_ENTITY.website} className="h-8 w-auto mx-auto brightness-0 invert opacity-90" />
          </Link>
          <p className="mt-4 text-xs sm:text-sm">
            {LEGAL_ENTITY.website} is owned and operated by {LEGAL_ENTITY.name} (ABN {LEGAL_ENTITY.abn}).
          </p>
          <p className="mt-2 text-[11px] leading-relaxed max-w-2xl mx-auto text-gray-500">
            {AREA_DATA_NOTE} CompareYourOptions.com.au is a free comparison service and does
            not itself supply or install systems.
          </p>
          <div className="mt-4 flex justify-center gap-4 text-xs">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <span>•</span>
            <Link to="/about" className="hover:text-white">About</Link>
          </div>
          <p className="mt-6 text-[11px] text-gray-600">
            © {new Date().getFullYear()} {LEGAL_ENTITY.website}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AreaPage;
